import React, { useState, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Clock,
  Check,
  X,
  Plus,
  ArrowsLeftRight,
  QrCode,
  Copy,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

export const TablesPage = () => {
  const { currentOutlet, tables, fetchMenuData, updateTableStatus } = useApp();
  const navigate = useNavigate();
  const [selectedTable, setSelectedTable] = useState(null);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [showQrDialog, setShowQrDialog] = useState(false);

  useEffect(() => {
    if (currentOutlet) {
      fetchMenuData();
      const interval = setInterval(fetchMenuData, 15000);
      return () => clearInterval(interval);
    }
  }, [currentOutlet, fetchMenuData]);

  const getTableColor = (status) => {
    switch (status) {
      case 'available': return 'table-available';
      case 'occupied': return 'table-occupied';
      case 'reserved': return 'table-reserved';
      default: return 'border-gray-300 bg-gray-50';
    }
  };

  const handleTableClick = (table) => {
    setSelectedTable(table);
    setShowTableDialog(true);
  };

  const handleNewOrder = () => {
    setShowTableDialog(false);
    navigate('/pos', { state: { tableId: selectedTable?.id, tableNumber: selectedTable?.table_number } });
  };

  const handleClearTable = async () => {
    if (selectedTable) {
      await updateTableStatus(selectedTable.id, 'available');
      setShowTableDialog(false);
    }
  };

  const tableCounts = {
    available: tables.filter(t => t.status === 'available').length,
    occupied: tables.filter(t => t.status === 'occupied').length,
    reserved: tables.filter(t => t.status === 'reserved').length,
  };

  if (!currentOutlet) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an outlet to view tables</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tables-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Table Layout</h1>
          <p className="text-muted-foreground mt-1">{currentOutlet?.name}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-accent-green"></span>
              <span>Available ({tableCounts.available})</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-accent-red"></span>
              <span>Occupied ({tableCounts.occupied})</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-accent-yellow"></span>
              <span>Reserved ({tableCounts.reserved})</span>
            </div>
          </div>
        </div>
      </div>

      {/* Table Grid */}
      <div className="bg-white rounded-lg border border-border p-8 min-h-[500px]">
        <div className="grid grid-cols-5 gap-6">
          {tables.map((table) => (
            <button
              key={table.id}
              onClick={() => handleTableClick(table)}
              className={`table-cell aspect-square flex-col gap-2 ${getTableColor(table.status)} ${
                table.shape === 'round' ? 'rounded-full' : 'rounded-lg'
              }`}
              data-testid={`table-${table.table_number}`}
            >
              <span className="text-2xl font-heading font-bold">{table.table_number}</span>
              <div className="flex items-center gap-1 text-xs">
                <Users size={14} />
                <span>{table.capacity}</span>
              </div>
              {table.status === 'occupied' && table.current_order_id && (
                <span className="text-xs mt-1">Order Active</span>
              )}
            </button>
          ))}
        </div>

        {tables.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <p className="text-lg">No tables configured</p>
            <p className="text-sm">Add tables in Settings</p>
          </div>
        )}
      </div>

      {/* Table Dialog */}
      <Dialog open={showTableDialog} onOpenChange={setShowTableDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Table {selectedTable?.table_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Status</span>
              <span className={`status-badge ${
                selectedTable?.status === 'available' ? 'status-ready' :
                selectedTable?.status === 'occupied' ? 'status-new' :
                'status-preparing'
              }`}>
                {selectedTable?.status}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Capacity</span>
              <span>{selectedTable?.capacity} guests</span>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-4">
              {currentOutlet && selectedTable && (
                <Button
                  variant="outline"
                  className="col-span-2 h-10"
                  onClick={() => setShowQrDialog(true)}
                  data-testid="table-qr-btn"
                >
                  <QrCode size={16} className="mr-2" />
                  Show QR Code
                </Button>
              )}
              {selectedTable?.status === 'available' && (
                <Button
                  onClick={handleNewOrder}
                  className="col-span-2 h-12 bg-primary hover:bg-primary-hover text-white"
                  data-testid="start-order-btn"
                >
                  <Plus size={18} className="mr-2" />
                  Start Order
                </Button>
              )}
              
              {selectedTable?.status === 'occupied' && (
                <>
                  <Button
                    variant="outline"
                    className="h-12"
                    onClick={() => navigate(`/orders/${selectedTable?.current_order_id}`)}
                  >
                    View Order
                  </Button>
                  <Button
                    onClick={handleClearTable}
                    className="h-12 bg-accent-green hover:bg-green-600 text-white"
                    data-testid="clear-table-btn"
                  >
                    <Check size={18} className="mr-2" />
                    Clear Table
                  </Button>
                </>
              )}

              {selectedTable?.status === 'reserved' && (
                <>
                  <Button
                    onClick={handleNewOrder}
                    className="h-12 bg-primary hover:bg-primary-hover text-white"
                  >
                    Seat & Order
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleClearTable}
                    className="h-12"
                  >
                    Cancel Reservation
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQrDialog} onOpenChange={setShowQrDialog}>
        <DialogContent className="max-w-sm" data-testid="qr-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Table {selectedTable?.table_number} · QR Menu
            </DialogTitle>
          </DialogHeader>
          {currentOutlet && selectedTable && (() => {
            const baseUrl = (currentOutlet.public_base_url || '').replace(/\/$/, '') || window.location.origin;
            const qrUrl = `${baseUrl}/qr/${currentOutlet.id}?table=${selectedTable.id}`;
            return (
            <div className="flex flex-col items-center py-4 space-y-4">
              <div className="bg-white p-4 rounded-2xl" data-testid="qr-svg-wrapper">
                <QRCodeSVG
                  value={qrUrl}
                  size={220}
                  level="M"
                  marginSize={2}
                />
              </div>
              <p className="text-xs text-center text-muted-foreground break-all px-2">
                {qrUrl}
              </p>
              <div className="flex gap-2 w-full">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    navigator.clipboard?.writeText(qrUrl);
                    toast.success('Link copied');
                  }}
                  data-testid="qr-copy-link"
                >
                  <Copy size={14} className="mr-2" /> Copy Link
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.print()}
                  data-testid="qr-print-btn"
                >
                  Print
                </Button>
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TablesPage;
